import React, { useRef, useState } from "react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { Share2, Download, X, ArrowUp, ArrowDown, Minus, Trophy } from "lucide-react";

const Movement = ({ movement, hasHistory }) => {
  if (!hasHistory) return <span className="text-xs uppercase tracking-widest text-zinc-400">New entry</span>;
  if (movement > 0) return <span className="flex items-center gap-1 text-green-400 font-display"><ArrowUp size={18} /> {movement} since last game</span>;
  if (movement < 0) return <span className="flex items-center gap-1 text-red-400 font-display"><ArrowDown size={18} /> {Math.abs(movement)} since last game</span>;
  return <span className="flex items-center gap-1 text-zinc-400 font-display"><Minus size={16} /> No change</span>;
};

export const GroupRankShareCard = ({ group, tournamentName }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const cardRef = useRef(null);

  const handleDownload = async () => {
    if (!cardRef.current || busy) return;
    setBusy(true);
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, cacheBust: true, skipFonts: true, backgroundColor: "#09090b" });
      const link = document.createElement("a");
      link.download = `jinxing-rank-${group.name.replace(/\s+/g, "-").toLowerCase()}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Rank card downloaded — share away!");
    } catch {
      toast.error("Couldn't generate the image. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const openModal = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(true);
  };

  return (
    <>
      <button
        onClick={openModal}
        data-testid={`share-rank-btn-${group.group_id}`}
        title="Share my rank"
        className="w-8 h-8 flex items-center justify-center border border-zinc-800 text-zinc-400 hover:border-primary-accent hover:text-primary-accent transition-colors flex-shrink-0"
      >
        <Share2 size={14} />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="share-rank-modal"
          onClick={(e) => { e.stopPropagation(); setOpen(false); }}
        >
          <div className="max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div className="label-tag text-zinc-400">Share Card Preview</div>
              <button
                data-testid="close-share-rank"
                onClick={() => setOpen(false)}
                className="w-8 h-8 flex items-center justify-center border border-zinc-800 text-zinc-400 hover:border-white/30"
              >
                <X size={14} />
              </button>
            </div>

            {/* The card that gets exported */}
            <div ref={cardRef} className="bg-zinc-950 grid-bg p-7 border border-white/10" data-testid="share-rank-card">
              <div className="flex items-center gap-2.5 mb-7">
                <div className="w-8 h-8 bg-primary-accent flex items-center justify-center">
                  <Trophy size={18} />
                </div>
                <span className="font-display text-lg tracking-wider">JINXING SPORTS</span>
              </div>

              <div className="label-tag mb-2">My Standing</div>
              <div className="flex items-center gap-2.5 mb-1">
                <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: group.color || "var(--primary)" }} />
                <span className="font-display text-2xl uppercase tracking-wide leading-none">{group.name}</span>
              </div>
              <div className="text-zinc-500 text-xs uppercase tracking-widest mb-6">{tournamentName}</div>

              <div className="flex items-end gap-4 mb-5">
                <div className="font-display text-7xl text-primary-accent leading-[0.85]">#{group.rank}</div>
                <div className="pb-1.5">
                  <div className="text-zinc-400 text-sm">of {group.member_count} {group.member_count === 1 ? "member" : "members"}</div>
                  <div className="font-display text-2xl">{group.points} pts</div>
                </div>
              </div>

              <div className="mb-7 text-sm">
                <Movement movement={group.movement} hasHistory={group.has_history} />
              </div>

              <div className="border-t border-white/10 pt-3 text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                Predict · Survive · Conquer · jinxingsports.com
              </div>
            </div>

            <button
              onClick={handleDownload}
              disabled={busy}
              data-testid="download-rank-card-btn"
              className="w-full mt-3 bg-primary-accent text-white py-3 font-display tracking-widest text-sm uppercase hover:bg-[var(--primary-hover)] disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Download size={14} /> {busy ? "Generating…" : "Download Image"}
            </button>
          </div>
        </div>
      )}
    </>
  );
};
